import React, { useState, useEffect } from "react";
import {
  Breadcrumbs,
  Checkbox,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Badge,
  Button,
  createStyles,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import dayjs from "dayjs";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import OverrideForm from "../components/OverrideForm";
import { useLocationAttendanceApiService } from "../services/api/LocationAttendanceApiService";
import {
  useCreateShiftClockApiService,
  useNoShowOverrideApiService,
} from "../services/api/ShiftClockApiService";
import { ClockType } from "../services/model/ShiftClock";
import ClockOverrideSchema from "../services/validator/ClockOverrideSchema";
import {
  getStatusBadge,
  formatTimeShort,
  formatTimeHHMM,
  buildIsoTimestamp,
} from "../utils/attendanceUtils";
import StyledBadge from "../elements/StyledBadge";
import { useBreadcrumbStyles } from "../styles/sharedStyles";
import { useSiteEmployeesApiService } from "../services/api/SiteEmployeesApiService";
import {
  useCreateScheduleOverrideApiService,
  useApproveScheduleOverrideApiService,
} from "../services/api/ScheduleOverridesApiService";
import { OverrideType } from "../services/model/ScheduleOverride";
import { useResolveExceptionApiService } from "../services/api/AttendanceExceptionsApiService";
import {
  ResolutionAction,
  isResolutionAction,
} from "../services/request/AttendanceExceptionsRequest";
import AccessGuard from "../../../common/uac/AccessGuard";

const useStyles = createStyles(() => ({
  onSiteDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#1DD19C",
    display: "inline-block",
  },
}));

interface FormValues {
  clock_type: string;
  timestamp: string;
  reason: string;
  no_show_reason: string;
  no_show_reason_other: string;
}

const NO_SHOW_REASON_OPTIONS = [
  { value: "Sick", label: "Sick" },
  { value: "Family Responsibility", label: "Family Responsibility" },
  { value: "Other", label: "Other" },
];

const EmployeeOverridePageInner: React.FC = () => {
  const { classes } = useStyles();
  const { classes: bcClasses } = useBreadcrumbStyles();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { locationId, employeeId } = useParams<{
    locationId: string;
    employeeId: string;
  }>();
  const [searchParams] = useSearchParams();
  const shiftConfigParam = searchParams.get("shift_config");
  // When routed from a row's "Clock out" action, preselect the clock-out flow.
  const actionParam = searchParams.get("action");
  const pendingExceptionId = searchParams.get("exception_id");
  const rawExceptionAction = searchParams.get("exception_action");
  const pendingExceptionAction: ResolutionAction | null = isResolutionAction(
    rawExceptionAction
  )
    ? rawExceptionAction
    : null;
  const { mutateAsync: resolvePendingException } =
    useResolveExceptionApiService();

  const today = dayjs().format("YYYY-MM-DD");

  const { data, isLoading } = useLocationAttendanceApiService({
    locationId: locationId || "",
    date: today,
    enabled: Boolean(locationId),
  });

  const location = data?.location;
  const locationBackUrl = location?.site
    ? `/time-attendance/shift-site/${location.site}`
    : `/time-attendance/${locationId}`;
  const records = data?.records || [];
  // Match by both employee ID and shift config to get the correct shift record
  const employee = shiftConfigParam
    ? records.find(
        (r) =>
          r.employee_id === Number(employeeId) &&
          r.shift_config_id === shiftConfigParam
      ) || records.find((r) => r.employee_id === Number(employeeId))
    : records.find((r) => r.employee_id === Number(employeeId));

  const {
    mutate: createShiftClock,
    mutateAsync: clockInReplacementAsync,
    isLoading: isSubmitting,
  } = useCreateShiftClockApiService();
  const { mutate: createNoShowOverride, isLoading: isSubmittingNoShow } =
    useNoShowOverrideApiService();

  const now = new Date();

  const form = useForm<FormValues>({
    validate: yupResolver(ClockOverrideSchema),
    initialValues: {
      clock_type: actionParam === "clock_out" ? "clock_out" : "",
      timestamp: formatTimeHHMM(now),
      reason: "",
      no_show_reason: "",
      no_show_reason_other: "",
    },
  });

  // Track timestamp as a Date object for TimeInput
  const [timestampDate, setTimestampDate] = useState<Date | null>(now);

  const isWrongSite = employee?.valid_location === false;

  const [activeTab, setActiveTab] = useState(0);
  const [tabInitialized, setTabInitialized] = useState(false);
  const [selectedReplacement, setSelectedReplacement] = useState<string | null>(
    null
  );
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(employeeSearch, 300);
  const [clockInReplacement, setClockInReplacement] = useState(false);
  const [replacementClockTime, setReplacementClockTime] = useState<Date | null>(
    new Date()
  );
  const [replacementClockReason, setReplacementClockReason] = useState("");

  const tabParam = searchParams.get("tab");

  // Tab default: an explicit "Clock out" action wins (stay on the override
  // details tab with clock-out preselected); otherwise auto-select "Find a
  // replacement" for wrong-site/unverified or when tab=replacement in URL.
  useEffect(() => {
    if (employee && !tabInitialized) {
      if (actionParam === "clock_out") {
        setActiveTab(0);
      } else if (
        employee.valid_location === false ||
        tabParam === "replacement"
      ) {
        setActiveTab(1);
      }
      setTabInitialized(true);
    }
  }, [employee, tabInitialized, tabParam, actionParam]);

  const { data: siteEmployees, isLoading: isLoadingEmployees } =
    useSiteEmployeesApiService({
      locationId: locationId || "",
      exclude: Number(employeeId) || undefined,
      search: debouncedSearch || undefined,
      enabled: Boolean(locationId) && activeTab === 1,
    });

  const { mutate: createOverride, isLoading: isSubmittingOverride } =
    useCreateScheduleOverrideApiService();

  const { mutateAsync: approveOverride } =
    useApproveScheduleOverrideApiService();

  const handleSubmit = (values: FormValues): void => {
    if (!locationId || !employeeId) return;

    if (values.clock_type === "no_show") {
      const reason =
        values.no_show_reason === "Other"
          ? values.no_show_reason_other
          : values.no_show_reason;

      createNoShowOverride(
        {
          employee_id: Number(employeeId),
          shift_location: locationId,
          shift_config: employee?.shift_config_id || null,
          reason,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(["AttendanceExceptionsApiService"]);
            showNotification({
              title: "Success",
              message: "Employee has been marked as No Show.",
              color: "green",
            });
            navigate(locationBackUrl);
          },
          onError: () => {
            showNotification({
              title: "Error",
              message: "Failed to mark as No Show. Please try again.",
              color: "red",
            });
          },
        }
      );
      return;
    }

    createShiftClock(
      {
        employee_id: Number(employeeId),
        shift_location: locationId,
        shift_config: employee?.shift_config_id || null,
        clock_type: values.clock_type as ClockType,
        timestamp: buildIsoTimestamp(values.timestamp),
        reason: values.reason,
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Employee clock record has been updated successfully.",
            color: "green",
          });
          navigate(locationBackUrl);
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to update clock record. Please try again.",
            color: "red",
          });
        },
      }
    );
  };

  const handleCancel = (): void => {
    navigate(locationBackUrl);
  };

  const handleReplacementSubmit = (): void => {
    if (!locationId || !employeeId || !selectedReplacement) return;

    // For wrong-site: current employee covers the no-show employee's shift
    // For normal: selected employee covers the current employee's shift
    const coveredId = isWrongSite
      ? Number(selectedReplacement)
      : Number(employeeId);
    const coveringId = isWrongSite
      ? Number(employeeId)
      : Number(selectedReplacement);

    // Use the covered employee's shift window (not the wrong-site employee's)
    const coveredRecord = isWrongSite
      ? records.find((r) => String(r.employee_id) === selectedReplacement)
      : null;
    const shiftStart = isWrongSite
      ? coveredRecord?.shift_start ?? null
      : employee?.shift_start ?? null;
    const shiftEnd = isWrongSite
      ? coveredRecord?.shift_end ?? null
      : employee?.shift_end ?? null;

    const replacementShiftConfig = isWrongSite
      ? coveredRecord?.shift_config_id ?? null
      : employee?.shift_config_id ?? null;

    createOverride(
      {
        covered_employee: coveredId,
        covering_employee: coveringId,
        shift_location: locationId,
        override_type: OverrideType.SICK_COVER,
        override_date: today,
        shift_start: shiftStart ? String(shiftStart) : null,
        shift_end: shiftEnd ? String(shiftEnd) : null,
        reason: isWrongSite
          ? "Wrong site employee replacement"
          : "Employee replacement",
      },
      {
        onSuccess: async (override) => {
          // Auto-approve for wrong-site replacements (admin action)
          if (isWrongSite && override.id) {
            try {
              await approveOverride(override.id);
            } catch {
              showNotification({
                title: "Warning",
                message:
                  "Replacement was created, but auto-approval failed. Please approve it manually.",
                color: "yellow",
              });
            }
          }

          if (clockInReplacement && replacementClockTime) {
            try {
              await clockInReplacementAsync({
                employee_id: coveringId,
                shift_location: locationId,
                shift_config: replacementShiftConfig,
                clock_type: "clock_in" as ClockType,
                timestamp: buildIsoTimestamp(
                  formatTimeHHMM(replacementClockTime)
                ),
                reason: replacementClockReason,
              });
            } catch {
              queryClient.invalidateQueries(["AttendanceExceptionsApiService"]);
              showNotification({
                title: "Warning",
                message:
                  "Replacement assigned, but clock-in failed. Please clock them in manually.",
                color: "yellow",
              });
              navigate(locationBackUrl);
              return;
            }
          }

          if (pendingExceptionId) {
            try {
              await resolvePendingException({
                id: pendingExceptionId,
                body: { action: pendingExceptionAction || "replacement" },
              });
            } catch {
              // Soft-fail: the override succeeded; surface a notice but don't roll back.
              showNotification({
                title: "Warning",
                message:
                  "Replacement assigned, but failed to mark the exception resolved. Reopen it from the exceptions list if needed.",
                color: "yellow",
              });
            }
          }

          queryClient.invalidateQueries(["AttendanceExceptionsApiService"]);
          showNotification({
            title: "Success",
            message: clockInReplacement
              ? "Replacement assigned and clocked in successfully."
              : "Replacement employee has been assigned successfully.",
            color: "green",
          });
          navigate(locationBackUrl);
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to assign replacement. Please try again.",
            color: "red",
          });
        },
      }
    );
  };

  const isOnSite =
    Boolean(employee?.clock_in_time) && !employee?.clock_out_time;

  const statusBadge = employee ? getStatusBadge(employee) : null;

  return (
    <PageContainer>
      <LoadingOverlay visible={isLoading} />

      <PageHeading title="Time & Attendance" />

      {/* Breadcrumbs */}
      <Breadcrumbs
        separator=">"
        classNames={{
          root: "mt-2 mb-6",
          breadcrumb: "hover:no-underline text-sm",
          separator: "text-gray-400",
        }}
        styles={{
          root: { flexWrap: "wrap", rowGap: 4 },
          breadcrumb: { whiteSpace: "normal", overflowWrap: "anywhere" },
        }}
      >
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/time-attendance")}
        >
          Time &amp; Attendance
        </Text>
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate(locationBackUrl)}
        >
          {location?.name || "..."}
        </Text>
        <Text className={bcClasses.breadcrumbCurrent}>
          {employee?.employee_name || "..."}
        </Text>
      </Breadcrumbs>

      <Stack spacing="lg">
        {/* Employee Info Section */}
        <Paper withBorder p="lg" radius="md">
          <Stack spacing="sm">
            <Text weight={700} size="lg">
              {employee?.employee_name || "—"}
            </Text>

            <Text size="sm" color="dimmed">
              Date: {dayjs().format("dddd, MMMM D, YYYY")}
            </Text>

            <Group spacing="xs">
              <Text size="sm" color="dimmed">
                Clock In:
              </Text>
              <Text size="sm" weight={500}>
                {employee?.clock_in_time
                  ? formatTimeShort(employee.clock_in_time)
                  : "Not clocked in"}
              </Text>
            </Group>

            <Group spacing="xs">
              <Text size="sm" color="dimmed">
                Clock Out:
              </Text>
              <Text size="sm" weight={500}>
                {employee?.clock_out_time
                  ? formatTimeShort(employee.clock_out_time)
                  : "Not clocked out"}
              </Text>
            </Group>

            {statusBadge && (
              <Group spacing="xs">
                <Text size="sm" color="dimmed">
                  Status:
                </Text>
                <StyledBadge
                  variant={statusBadge.variant}
                  label={statusBadge.label}
                />
              </Group>
            )}

            {isWrongSite && (
              <StyledBadge variant="unverified" label="Unverified location" />
            )}

            {isOnSite && (
              <Group spacing="xs">
                <span className={classes.onSiteDot} />
                <Text size="sm" weight={500} color="#14926D">
                  On Site
                </Text>
                <Badge
                  size="xs"
                  radius="xl"
                  style={{
                    backgroundColor: "#e8faf5",
                    color: "#14926d",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Live
                </Badge>
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Update Employee Information Section */}
        <Stack spacing="sm">
          <Text weight={600} size="md">
            Update Employee Information
          </Text>

          {activeTab === 0 ? (
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack spacing="lg">
                <Tabs active={activeTab} onTabChange={setActiveTab}>
                  <Tabs.Tab label="Override shift details">
                    <div className="pt-4">
                      <Stack spacing="md">
                        <Select
                          label="Type"
                          placeholder="Select override type"
                          required
                          data={[
                            { value: "clock_in", label: "Clock In Time" },
                            { value: "clock_out", label: "Clock Out Time" },
                            { value: "no_show", label: "No Show" },
                          ]}
                          value={form.values.clock_type || null}
                          onChange={(value) => {
                            form.setFieldValue("clock_type", value ?? "");
                            if (value === "no_show") {
                              form.setFieldValue("timestamp", "");
                              form.setFieldValue("reason", "");
                            } else {
                              form.setFieldValue("no_show_reason", "");
                              form.setFieldValue("no_show_reason_other", "");
                              if (!form.values.timestamp && timestampDate) {
                                form.setFieldValue(
                                  "timestamp",
                                  formatTimeHHMM(timestampDate)
                                );
                              }
                            }
                          }}
                          error={form.errors.clock_type as string | undefined}
                          size="sm"
                        />
                        {form.values.clock_type === "no_show" ? (
                          <Stack spacing="md">
                            <Select
                              label="Reason"
                              placeholder="Select a reason"
                              required
                              data={NO_SHOW_REASON_OPTIONS}
                              value={form.values.no_show_reason || null}
                              onChange={(value) =>
                                form.setFieldValue(
                                  "no_show_reason",
                                  value ?? ""
                                )
                              }
                              error={
                                form.errors.no_show_reason as string | undefined
                              }
                              size="sm"
                            />
                            {form.values.no_show_reason === "Other" && (
                              <TextInput
                                label="Specify reason"
                                placeholder="Enter reason"
                                required
                                value={form.values.no_show_reason_other}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "no_show_reason_other",
                                    e.currentTarget.value
                                  )
                                }
                                error={
                                  form.errors.no_show_reason_other as
                                    | string
                                    | undefined
                                }
                                size="sm"
                              />
                            )}
                          </Stack>
                        ) : (
                          <OverrideForm
                            timestamp={timestampDate}
                            onTimestampChange={(value: Date) => {
                              setTimestampDate(value);
                              form.setFieldValue(
                                "timestamp",
                                formatTimeHHMM(value)
                              );
                            }}
                            reason={form.values.reason}
                            onReasonChange={(value) =>
                              form.setFieldValue("reason", value)
                            }
                            errors={{
                              timestamp: form.errors.timestamp as
                                | string
                                | undefined,
                              reason: form.errors.reason as string | undefined,
                            }}
                          />
                        )}
                      </Stack>
                    </div>
                  </Tabs.Tab>

                  <Tabs.Tab label="Find a replacement">
                    <div className="pt-4" />
                  </Tabs.Tab>
                </Tabs>

                <Group position="right" spacing="sm">
                  <Button
                    variant="default"
                    onClick={handleCancel}
                    disabled={isSubmitting || isSubmittingNoShow}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isSubmitting || isSubmittingNoShow}
                  >
                    Save Changes
                  </Button>
                </Group>
              </Stack>
            </form>
          ) : (
            <Stack spacing="lg">
              <Tabs active={activeTab} onTabChange={setActiveTab}>
                <Tabs.Tab label="Override shift details">
                  <div className="pt-4" />
                </Tabs.Tab>

                <Tabs.Tab label="Find a replacement">
                  <div className="pt-4">
                    <Stack spacing="md">
                      {isWrongSite ? (
                        <Text size="sm" color="dimmed">
                          The location for {employee?.employee_name} could not
                          be verified at their assigned site. If they are
                          covering another post, select the employee they are
                          covering for:
                        </Text>
                      ) : (
                        <Text size="sm" color="dimmed">
                          Find a replacement for {employee?.employee_name}
                        </Text>
                      )}
                      <Select
                        label="Select employee"
                        placeholder="Name Surname"
                        required
                        searchable
                        nothingFound="No employees found"
                        data={[...(siteEmployees?.results || [])]
                          .sort(
                            (a, b) =>
                              Number(b.is_on_site || false) -
                              Number(a.is_on_site || false)
                          )
                          .map((emp) => ({
                            value: String(emp.employee_id),
                            label: emp.employee_name,
                            group: emp.is_on_site ? "On Site" : "Not on site",
                          }))}
                        value={selectedReplacement}
                        onChange={setSelectedReplacement}
                        onSearchChange={setEmployeeSearch}
                        filter={() => true}
                        size="sm"
                        disabled={isLoadingEmployees}
                      />

                      <Checkbox
                        label="Clock in replacement now"
                        checked={clockInReplacement}
                        onChange={(e) =>
                          setClockInReplacement(e.currentTarget.checked)
                        }
                      />

                      {clockInReplacement && (
                        <OverrideForm
                          timestamp={replacementClockTime}
                          onTimestampChange={setReplacementClockTime}
                          reason={replacementClockReason}
                          onReasonChange={setReplacementClockReason}
                        />
                      )}
                    </Stack>
                  </div>
                </Tabs.Tab>
              </Tabs>

              <Group position="right" spacing="sm">
                <Button
                  variant="default"
                  onClick={handleCancel}
                  disabled={isSubmittingOverride || isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReplacementSubmit}
                  loading={isSubmittingOverride || isSubmitting}
                  disabled={
                    !selectedReplacement ||
                    (clockInReplacement &&
                      (!replacementClockTime || !replacementClockReason.trim()))
                  }
                >
                  Save Changes
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Stack>
    </PageContainer>
  );
};

const EmployeeOverridePage: React.FC = () => (
  <AccessGuard>
    <EmployeeOverridePageInner />
  </AccessGuard>
);

export default EmployeeOverridePage;
